'use client'

import { useEffect, useState, ChangeEvent } from 'react'
import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import { useFirstUserInteraction } from '@eversports/klimt-utilities/use-first-user-interaction'

import getUserTrackingPreferences from './helpers/get-user-tracking-preferences'
import setUserTrackingPreferences from './helpers/set-user-tracking-preferences'
import setAmplitudeCookies from './helpers/set-amplitude-cookies'
import handleLocationReload from './helpers/handle-location-reload'
import { TrackingType } from './CookieBanner.types'
import CookieDialog from './CookieDialog'
import { DEFAULT_TRACKING_PREFERENCES } from './CookieBanner.constants'
import { CookieButtons } from './CookieButtons'
import TrackingOptions from './TrackingOptions'

const CookieBanner = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [showCookiePreferences, setShowCookiePreferences] = useState(false)
  const [trackingPreferences, setTrackingPreferences] =
    useState<{ [key in TrackingType]: boolean }>(DEFAULT_TRACKING_PREFERENCES)

  const { amplitude } = useAmplitude()

  const toggleCookiePreferences = () => setShowCookiePreferences(true)
  const toggleCookieBanner = () => setIsDialogOpen(!isDialogOpen)

  const { handleFirstInteraction } = useFirstUserInteraction({
    element: window.document,
    handleInteraction: toggleCookieBanner,
  })

  const toggleTrackingOption = (type: TrackingType) => (event: ChangeEvent<HTMLInputElement>) => {
    setTrackingPreferences({ ...trackingPreferences, [type]: event.target.checked })
  }

  const handleClickAccept = () => {
    setUserTrackingPreferences({ [TrackingType.ANALYTICS]: true, [TrackingType.ADVERTISING]: true })
    setIsDialogOpen(false)
    handleLocationReload()
  }

  const handleClickConfirm = () => {
    setUserTrackingPreferences({
      [TrackingType.ANALYTICS]: trackingPreferences.ANALYTICS,
      [TrackingType.ADVERTISING]: trackingPreferences.ADVERTISING,
    })
    setIsDialogOpen(false)
    handleLocationReload()
  }

  useEffect(() => {
    const trackingPreferences = getUserTrackingPreferences()
    if (!trackingPreferences) return
    setTrackingPreferences({
      [TrackingType.ANALYTICS]: Boolean(trackingPreferences.ANALYTICS),
      [TrackingType.ADVERTISING]: Boolean(trackingPreferences.ADVERTISING),
    })
  }, [])

  useEffect(() => {
    // only check for analytics - we will check for advertising in the auth app
    const savedUserTrackingPreferences = getUserTrackingPreferences()
    const userHasSetTrackingPreferences = savedUserTrackingPreferences.ANALYTICS !== null
    if (userHasSetTrackingPreferences) return
    handleFirstInteraction()
  }, [])

  useEffect(() => {
    if (amplitude) {
      const deviceId = amplitude.getDeviceId()
      const sessionId = amplitude.getSessionId()
      setAmplitudeCookies({ deviceId, sessionId })
    }
  }, [amplitude])

  return (
    <CookieDialog isOpen={isDialogOpen} toggleCookieBanner={toggleCookieBanner}>
      <div className="flex flex-col gap-4">
        <TrackingOptions
          showCookiePreferences={showCookiePreferences}
          trackingPreferences={trackingPreferences}
          toggleOption={toggleTrackingOption}
        />
        <CookieButtons
          showCookiePreferences={showCookiePreferences}
          toggleCookiePreferences={toggleCookiePreferences}
          handleClickAccept={handleClickAccept}
          handleClickConfirm={handleClickConfirm}
        />
      </div>
    </CookieDialog>
  )
}

export default CookieBanner
